'use client';

import { useState, useRef } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { useSkillsStore } from '../store';
import { SkillGeneratorDialog } from './SkillGeneratorDialog';

interface SkillUploadDialogProps {
  mode: 'create' | 'edit';
  initialYaml?: string;
  skillId?: string;
  onClose: () => void;
}

export function SkillUploadDialog({ mode, initialYaml = '', skillId, onClose }: SkillUploadDialogProps) {
  const t = useTranslations('skills');
  const [yaml, setYaml] = useState(initialYaml);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { createSkill, updateSkill } = useSkillsStore((s) => s.actions);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setYaml(reader.result as string);
    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!yaml.trim()) {
      setError(t('skillUploadDialog.yamlRequired'));
      return;
    }
    setSubmitting(true);
    try {
      if (mode === 'edit' && skillId) {
        await updateSkill(skillId, yaml);
      } else {
        await createSkill(yaml);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('skillUploadDialog.operationFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogOverlay onClick={onClose} />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{mode === 'edit' ? t('skillUploadDialog.editTitle') : t('skillUploadDialog.createTitle')}</DialogTitle>
            <DialogDescription>
              {mode === 'edit'
                ? t('skillUploadDialog.editDescription')
                : t('skillUploadDialog.createDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <textarea
              value={yaml}
              onChange={(e) => setYaml(e.target.value)}
              placeholder={`id: my-skill\nname: My Skill\ncategory: custom\nprompt: |\n  Your skill prompt here\nmodel: claude-sonnet-4-6\n# Optional — make this skill agentic (re-entrant tool-use loop):\n# agentic: true\n# tool_allowlist: [search_knowledge, load_research]`}
              className="w-full h-64 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono resize-y focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              spellCheck={false}
            />

            <p className="text-xs text-muted-foreground">
              {t.rich('skillUploadDialog.agenticHint', {
                code: (chunks) => <code className="font-mono">{chunks}</code>,
              })}
            </p>

            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".yaml,.yml"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-xs text-muted-foreground underline hover:text-foreground transition-colors duration-150"
              >
                {t('skillUploadDialog.uploadFile')}
              </button>
              {mode === 'create' && (
                <button
                  type="button"
                  onClick={() => setGeneratorOpen(true)}
                  className="text-xs text-accent underline hover:text-accent/80 transition-colors duration-150"
                >
                  {t('skillUploadDialog.askAiToGenerate')}
                </button>
              )}
            </div>

            {error && (
              <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
              {t('skillUploadDialog.cancel')}
            </Button>
            <Button size="sm" onClick={handleSubmit} disabled={submitting || !yaml.trim()}>
              {submitting ? t('skillUploadDialog.saving') : mode === 'edit' ? t('skillUploadDialog.saveChanges') : t('skillUploadDialog.createSkill')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {generatorOpen && (
        <SkillGeneratorDialog
          onUseYaml={(generated) => setYaml(generated)}
          onClose={() => setGeneratorOpen(false)}
        />
      )}
    </>
  );
}
